import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, Loader2, XCircle, PartyPopper } from "lucide-react";
import axios from "axios";
import useUser from "@/hooks/useUser";
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useUser();
  const [status, setStatus] = useState("verifying"); // verifying, success, failed
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setStatus("failed");
        return;
      }

      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/payments/stripe/checkout/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === "paid") {
          setStatus("success");
          setPaymentData(response.data);
          
          // Refresh user data to get updated plan
          refresh();
          
          // Trigger confetti
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        } else if (response.data.payment_status === "unpaid") {
          // Still processing
          setTimeout(verifyPayment, 2000);
        } else {
          setStatus("failed");
        }
      } catch (error) {
        console.error("Payment verification error:", error);
        setStatus("failed");
      }
    };

    verifyPayment();
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center">
        {status === "verifying" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-violet-600 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verifying Payment...
            </h1>
            <p className="text-gray-600">
              Please wait while we confirm your payment.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <PartyPopper className="w-6 h-6 text-yellow-500" />
              <h1 className="text-2xl font-bold text-gray-900">
                Payment Successful!
              </h1>
              <PartyPopper className="w-6 h-6 text-yellow-500" />
            </div>
            <p className="text-gray-600 mb-6">
              Welcome to Pro! Your account has been upgraded.
            </p>
            
            {paymentData && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
                <p className="text-xl font-bold text-gray-900">
                  ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </p>
              </div>
            )}
            
            <Button 
              onClick={() => navigate("/dashboard")}
              className="w-full bg-violet-600 hover:bg-violet-700"
              size="lg"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Failed
            </h1>
            <p className="text-gray-600 mb-6">
              Something went wrong with your payment. Please try again.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/pricing")}
                className="w-full bg-violet-600 hover:bg-violet-700"
              >
                Try Again
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
